import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from './translations';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import TrainerDashboard from './components/dashboard/TrainerDashboard';
import ClientDashboard from './components/dashboard/ClientDashboard';
import ClientDetailPage from './pages/ClientDetailPage';
import LiveGymBoard from './components/LiveGymBoard';
import WorkoutPrintPage from './components/workout/WorkoutPrintPage';
import EoaEReportPage from './components/eoae/EoaEReportPage';
import EoaEPublicPage from './components/eoae/EoaEPublicPage';

// Dashboard Component - Routes to TrainerDashboard or ClientDashboard based on user role
const Dashboard = () => {
  const { user, logout } = useAuth();
  const { language, setLanguageForRole } = useLanguage();
  const { t } = useTranslation(language);
  const navigate = useNavigate();

  // Set language based on user role (admin/trainer → EN, client → ES)
  useEffect(() => {
    if (user?.role) {
      setLanguageForRole(user.role);
    }
  }, [user?.role, setLanguageForRole]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (user?.role === 'administrator' || user?.role === 'trainer') {
    return <TrainerDashboard user={user} onLogout={handleLogout} language={language} t={t} />;
  } else {
    return <ClientDashboard user={user} onLogout={handleLogout} language={language} t={t} />;
  }
};

// Protected Route Component - Requires authentication
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// Main App Component
function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/clients/:clientId" element={
                <ProtectedRoute>
                  <ClientDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/live-gym" element={
                <ProtectedRoute>
                  <LiveGymBoard />
                </ProtectedRoute>
              } />
              <Route path="/workout/:id/print" element={
                <ProtectedRoute>
                  <WorkoutPrintPage />
                </ProtectedRoute>
              } />
              <Route path="/eoae/:id" element={
                <ProtectedRoute>
                  <EoaEReportPage />
                </ProtectedRoute>
              } />
              <Route path="/eoae/:id/print" element={
                <ProtectedRoute>
                  <EoaEReportPage />
                </ProtectedRoute>
              } />
              <Route path="/share/eoae/:token" element={<EoaEPublicPage />} />
              <Route path="/" element={<Navigate to="/dashboard" />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
